import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

interface Toast {
  id: number;
  message: string;
  kind: 'info' | 'success' | 'error';
}

interface ToastCtx {
  toasts: Toast[];
  show: (message: string, kind?: Toast['kind']) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, kind }]);
    setTimeout(() => {
      setToasts(t => t.filter(x => x.id !== id));
    }, 3500);
  }, []);

  return (
    <Ctx.Provider value={{ toasts, show }}>
      {children}
      <ToastContainer toasts={toasts} />
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx.show;
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed top-4 left-0 right-0 z-50 pointer-events-none flex flex-col items-center gap-2 px-4">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`px-4 py-2.5 rounded-lg shadow-lg text-sm font-demi pointer-events-auto
                      animate-[slideDown_.25s_ease-out]
                      ${t.kind === 'success' ? 'bg-success text-white' :
                        t.kind === 'error'   ? 'bg-danger text-white'  :
                                               'bg-ink text-white'}`}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}
